/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Endpoint } from '../index';
import { z, ZodTypes } from '../zod';
import { GetOrganization } from './organization.get';

const Params = z.strictObject({ organizationId: ZodTypes.Id });

export const PatchOrganization = {
  id: 'organization.patch',
  tag: 'Organization',
  summary: 'Patch organization',
  description:
    'Only supply the values that will be changed.\n' +
    '\n' +
    'Supplying `undefined` is not allowed.\n' +
    '\n' +
    'Supplying `null` will delete the value (only allowed on nullable fields).\n' +
    'Doing so will result in `404 null_not_allowed`.\n' +
    '\n' +
    'Should the email be changed, the user will receive a verification email.',
  method: 'patch',
  flags: { isAuthorized: true },
  route: (params: z.infer<typeof Params>) => `/organization/${params.organizationId}`,
  request: {
    params: Params,
    body: {
      required: true,
      content: {
        'application/json': {
          schema: z.strictObject({
            name: ZodTypes.KubernetesIdentifier.optional(),
            description: ZodTypes.TrimmedString.min(1).nullable().optional(),
            companyName: ZodTypes.TrimmedString.min(1).optional(),
            email: ZodTypes.TrimmedString.min(1).email().nullable().optional(),
            vatNumber: ZodTypes.TrimmedString.min(1).nullable().optional(),
            language: ZodTypes.Language.optional(),
            address: z
              .strictObject({
                line1: ZodTypes.TrimmedString.min(1).optional(),
                line2: ZodTypes.TrimmedString.min(1).nullable().optional(),
                line3: ZodTypes.TrimmedString.min(1).nullable().optional(),
                postalCode: ZodTypes.TrimmedString.min(1).optional(),
                city: ZodTypes.TrimmedString.min(1).optional(),
                state: ZodTypes.TrimmedString.min(1).nullable().optional(),
                countryCode: ZodTypes.Country.optional(),
              })
              .optional(),
            thumbnail: ZodTypes.ThumbnailFormat.optional(),
          }),
        },
      },
    },
  },
  response: {
    200: {
      description: 'successful',
      content: {
        'application/json': {
          schema: GetOrganization.response['200'].content['application/json'].schema,
        },
      },
    },
  },
} as const satisfies Endpoint;
